import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../components/LoadingSpinner";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../components/ResponsiveDataTable";
import { fmtDuration } from "../lib/format";
import { fetchRecovery, MttrApiError } from "../services/mttrApi";
import type { GroupStats, RecoverySnapshot } from "../types/recovery";

/**
 * Mean Time To Recovery page (Push L 2026-05-06). Mirrors UptimePage's
 * structure but the metric is recovery time per resolved ticket rather
 * than uptime %.
 *
 * Source for v1: `_jcox.dbo.maintenance_ticket` auto-tickets only.
 * Future-extensible — when manual ops workflows ship, source-side
 * tickets get UNION'd into the same shape and this page picks them up
 * automatically.
 */
const RecoveryPage: React.FC = () => {
  const [snapshot, setSnapshot] = useState<RecoverySnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState<string>("7d");
  const [customerFilter, setCustomerFilter] = useState<string>("");

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const d = await fetchRecovery();
        if (active) setSnapshot(d);
      } catch (e) {
        if (active) setError(e instanceof Error ? e : new MttrApiError(String(e)));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const window = snapshot?.windows[windowKey];

  const customers = useMemo(() => {
    if (!window) return [""];
    const set = new Set<string>();
    for (const c of window.by_customer) set.add(c.label);
    return ["", ...Array.from(set).sort()];
  }, [window]);

  const filteredCustomers = useMemo(() => {
    if (!window) return [];
    const cust = customerFilter.toLowerCase();
    return window.by_customer.filter(
      (c) => !cust || c.label.toLowerCase() === cust,
    );
  }, [window, customerFilter]);

  if (loading && !snapshot) {
    return <LoadingSpinner label="loading recovery" />;
  }
  if (!snapshot) {
    return (
      <div className="p-4 md:p-6 text-sm text-error-700">
        Failed to load recovery.json. {error?.message ?? ""}
      </div>
    );
  }

  const windowKeys = Object.keys(snapshot.windows);

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">Mean Time To Recovery</h1>
          <div className="text-xs text-secondary-500 mt-0.5">
            Generated {new Date(snapshot.generated_utc).toLocaleString()} ·
            Source: {snapshot.source}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto min-w-0">
          {windowKeys.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setWindowKey(k)}
              className={
                "shrink-0 px-3 py-2 min-h-[44px] rounded-md text-sm border transition-colors " +
                (k === windowKey
                  ? "bg-primary-600 text-white border-primary-700"
                  : "bg-white border-secondary-300 hover:bg-secondary-50 active:bg-secondary-100")
              }
            >
              Last {k}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}

      {!window ? (
        <div className="text-sm text-secondary-500">
          No data for window <code>{windowKey}</code>.
        </div>
      ) : window.fleet.count === 0 ? (
        <FleetCard fleet={window.fleet} since={window.since} until={window.until} empty />
      ) : (
        <>
          <FleetCard fleet={window.fleet} since={window.since} until={window.until} />

          <section className="mt-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
              <h2 className="text-base font-bold pb-1 border-b-2 border-primary-300 flex-1">
                By customer ({filteredCustomers.length})
              </h2>
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="md:ml-3 px-3 py-2 min-h-[44px] border border-secondary-300 rounded-md text-sm bg-white"
              >
                {customers.map((c) => (
                  <option key={c || "(any)"} value={c}>
                    {c || "All customers"}
                  </option>
                ))}
              </select>
            </div>
            <ResponsiveDataTable
              columns={GROUP_COLUMNS}
              rows={filteredCustomers}
              getRowKey={(c) => c.label}
              mobileHeader={(c) => (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{c.label}</span>
                  <span className="text-xs text-secondary-600">
                    mean {fmtDuration(Math.round(c.mean_seconds))}
                  </span>
                </div>
              )}
            />
          </section>

          <section className="mt-6">
            <h2 className="text-base font-bold pb-1 border-b-2 border-primary-300 mb-2">
              By issue type ({window.by_issue_type.length})
            </h2>
            <ResponsiveDataTable
              columns={GROUP_COLUMNS}
              rows={window.by_issue_type}
              getRowKey={(s) => s.label}
              mobileHeader={(s) => (
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-xs font-semibold">
                    {s.label}
                  </code>
                  <span className="text-xs text-secondary-600">
                    mean {fmtDuration(Math.round(s.mean_seconds))}
                  </span>
                </div>
              )}
            />
          </section>
        </>
      )}
    </div>
  );
};

const FleetCard: React.FC<{
  fleet: GroupStats;
  since: string;
  until: string;
  empty?: boolean;
}> = ({ fleet, since, until, empty }) => (
  <section className="rounded-md border border-secondary-200 bg-white p-5">
    <div className="flex items-baseline justify-between flex-wrap gap-2">
      <h2 className="text-sm uppercase tracking-wide text-secondary-700 font-semibold">
        Fleet recovery
      </h2>
      <div className="text-xs text-secondary-500">
        {new Date(since).toLocaleDateString()} –{" "}
        {new Date(until).toLocaleDateString()} · {fleet.count} tickets resolved
      </div>
    </div>
    {empty ? (
      <div className="mt-3 text-sm text-secondary-500">
        No tickets resolved in this window.
      </div>
    ) : (
      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Mean" seconds={fleet.mean_seconds} accent />
        <Stat label="Median" seconds={fleet.median_seconds} />
        <Stat label="Worst" seconds={fleet.max_seconds} />
      </div>
    )}
  </section>
);

const Stat: React.FC<{
  label: string;
  seconds: number;
  accent?: boolean;
}> = ({ label, seconds, accent }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-secondary-500">
      {label}
    </div>
    <div
      className={
        "font-bold " + (accent ? "text-3xl text-primary-700" : "text-xl text-secondary-900")
      }
    >
      {fmtDuration(Math.round(seconds))}
    </div>
  </div>
);

const GROUP_COLUMNS: ResponsiveColumn<GroupStats>[] = [
  {
    key: "label",
    header: "Group",
    hideOnMobile: true,
    cell: (s) => <span className="font-semibold">{s.label}</span>,
  },
  {
    key: "count",
    header: "Tickets",
    className: "text-right",
    cell: (s) => s.count,
  },
  {
    key: "mean",
    header: "Mean",
    className: "text-right",
    hideOnMobile: true,
    cell: (s) => fmtDuration(Math.round(s.mean_seconds)),
  },
  {
    key: "median",
    header: "Median",
    className: "text-right",
    cell: (s) => fmtDuration(Math.round(s.median_seconds)),
  },
  {
    key: "max",
    header: "Worst",
    className: "text-right",
    cell: (s) => fmtDuration(s.max_seconds),
  },
];

export default RecoveryPage;
